import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { AllBusiness, isAllBusiness, useAuthenticatedAPI } from "@/api";
import { useAuth } from "@/Auth";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { useBusinessesStatusGetter } from "@/hooks/useGetBusinessStatus";
import { useSFullApp } from "@/hooks/useSFullApp";
import { sFullAppContext } from "./SFullAppContext";
import { savedSLanguage } from "./useSEmbed";

type BusinessScope = { businessContext: string; businessIds: string[] };

export function SFullAppProvider({ children }: React.PropsWithChildren) {
  const api = useAuthenticatedAPI();
  const auth = useAuth();
  const businesses = useGetBusinesses();
  const businessContext = useBusinessContext();
  const getBusinessStatus = useBusinessesStatusGetter();
  const flag = useSFullApp();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [instanceId] = React.useState(() => crypto.randomUUID());
  // Pin the tab's selection. Background /self updates from another tab must not issue new S tokens.
  const [scope, setScope] = React.useState<BusinessScope>(() => ({
    businessContext: businessContext.business?.id ?? "",
    businessIds: isAllBusiness(businessContext.business?.id ?? "")
      ? (businesses.data ?? []).map((b) => b.id)
      : businessContext.business
        ? [businessContext.business.id]
        : [],
  }));
  const generation = React.useRef(0);
  const [revision, setRevision] = React.useState(0);
  const switching = React.useRef(false);
  const mounted = React.useRef(true);
  const [isSwitching, setIsSwitching] = React.useState(false);
  const [switchError, setSwitchError] = React.useState<Error | null>(null);
  const isFullAppPath = location.pathname === "/home" || location.pathname.startsWith("/home/");
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
    };
  }, []);

  const embedSession = useQuery({
    queryKey: [
      "sEmbedSession",
      "full-app",
      auth.user?.id,
      scope.businessContext,
      scope.businessIds,
      instanceId,
      revision,
    ],
    queryFn: async ({ signal }) => {
      const requestGeneration = generation.current;
      const session = await api.createSEmbedSession(
        {
          CreateSEmbedSessionRequest: { ...scope, fullApp: true, language: savedSLanguage() },
        },
        { signal },
      );
      if (!mounted.current || requestGeneration !== generation.current) {
        throw new Error("An older embed session was superseded");
      }
      if (
        !session.dataContext ||
        session.businessContext !== scope.businessContext ||
        !session.businessIds ||
        [...session.businessIds].sort().join(",") !== [...scope.businessIds].sort().join(",")
      ) {
        throw new Error("The embed session does not match this tab's business selection");
      }
      return session;
    },
    enabled: flag.enabled && isFullAppPath && scope.businessIds.length > 0 && !isSwitching,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    meta: { preventDefaultErrorHandling: true },
  });
  const latestSession = React.useRef(embedSession);
  latestSession.current = embedSession;
  const canPostToken = React.useCallback(
    () =>
      mounted.current &&
      !switching.current &&
      latestSession.current.isSuccess &&
      !latestSession.current.isFetching,
    [],
  );

  const switchBusiness = React.useCallback(
    async (id: string) => {
      if (switching.current) return;
      const target = isAllBusiness(id) ? undefined : businesses.data?.find((b) => b.id === id);
      if (
        (!isAllBusiness(id) && !target) ||
        getBusinessStatus(isAllBusiness(id) ? AllBusiness : target!) !== "available"
      ) {
        const error = new Error(
          "This business is not available yet. Check its installation and license in Settings.",
        );
        setSwitchError(error);
        throw error;
      }
      switching.current = true;
      generation.current += 1;
      setIsSwitching(true);
      setSwitchError(null);
      // Remove all previous full-app sessions, including an earlier visit to this same selection.
      await queryClient.cancelQueries({ queryKey: ["sEmbedSession", "full-app"] });
      queryClient.removeQueries({ queryKey: ["sEmbedSession", "full-app"] });
      try {
        await businessContext.setBusiness(id);
        await queryClient.invalidateQueries({ queryKey: ["self"] });
        if (!mounted.current) return;
        setScope({
          businessContext: id,
          businessIds: isAllBusiness(id) ? (businesses.data ?? []).map((b) => b.id) : [id],
        });
        setRevision(generation.current);
      } catch (error) {
        if (mounted.current)
          setSwitchError(error instanceof Error ? error : new Error("Could not change business"));
        throw error;
      } finally {
        switching.current = false;
        if (mounted.current) setIsSwitching(false);
      }
    },
    [businesses.data, businessContext, getBusinessStatus, queryClient],
  );

  return (
    <sFullAppContext.Provider
      value={{
        enabled: flag.enabled,
        embedSession,
        isSwitching,
        switchError,
        switchBusiness,
        canPostToken,
      }}
    >
      {children}
    </sFullAppContext.Provider>
  );
}
